import { z } from 'zod';

/**
 * **Dossiê da empresa** (issue #202) — o que a ação "Pesquisar empresa" do card
 * do lead entrega.
 *
 * A primeira versão devolvia PROSA: um briefing de 250 palavras gravado como
 * nota. O vendedor não conseguia clicar no site, não via as redes, não sabia a
 * reputação e não tinha com quem falar. Esta versão troca prosa por dados.
 *
 * ## O documento é organizado por FONTE, não por tema
 *
 * `sources.places`, `sources.tripadvisor`, `sources.cnpj`, … Cada coletor
 * escreve só o seu bloco, com o próprio `status`, o próprio `fetchedAt` e o
 * próprio TTL. Três consequências que motivaram a escolha:
 *
 * 1. **Procedência sem esforço** — o bloco É a origem. Não é preciso carimbar
 *    cada valor para o vendedor saber de onde veio "nota 4,3".
 * 2. **Falha isolada e visível** — uma fonte fora do ar vira um card com o
 *    motivo, em vez de sumir e fingir que a empresa não tem aquele dado.
 * 3. **Fonte nova é um bloco novo** — não uma remodelagem do schema.
 *
 * O que mistura fontes (contatos unificados, pontuação, conflitos) vive em
 * `consolidated`, e aí sim cada valor carrega o envelope `Sourced`.
 */

// --- Procedência ------------------------------------------------------------

/**
 * De onde um valor consolidado veio. A distinção que importa de verdade é
 * `AI_INFERENCE` contra todo o resto: um dossiê que mistura "nota 4,3 do
 * Google" com "provavelmente trabalha com congelados" sem marcar a diferença
 * faz o vendedor ligar citando um chute.
 */
export const CRM_DOSSIER_SOURCES = [
  'PLACES',
  'TRIPADVISOR',
  'CNPJ',
  'WEBSITE',
  'WEB_SEARCH',
  'AI_INFERENCE',
] as const;
export const CrmDossierSourceSchema = z.enum(CRM_DOSSIER_SOURCES);
export type CrmDossierSource = z.infer<typeof CrmDossierSourceSchema>;

export const CRM_DOSSIER_CONFIDENCE = ['HIGH', 'MEDIUM', 'LOW'] as const;
export const CrmDossierConfidenceSchema = z.enum(CRM_DOSSIER_CONFIDENCE);
export type CrmDossierConfidence = z.infer<typeof CrmDossierConfidenceSchema>;

/**
 * Estado de um coletor. `NO_MATCH` e `NOT_CONFIGURED` são estados de sucesso da
 * chamada, não erros: "o TripAdvisor não conhece esta empresa" e "a chave não
 * está configurada" levam o vendedor a conclusões OPOSTAS, e colapsar os dois
 * em "sem dados" esconde justamente a informação útil.
 */
export const CRM_DOSSIER_STATUS = [
  'OK',
  'NO_MATCH',
  'NOT_CONFIGURED',
  'FAILED',
  'SKIPPED',
] as const;
export const CrmDossierStatusSchema = z.enum(CRM_DOSSIER_STATUS);
export type CrmDossierStatus = z.infer<typeof CrmDossierStatusSchema>;

/** Envelope de valor consolidado: o dado + de onde veio + quanto confiar. */
export function crmSourcedSchema<T extends z.ZodType>(
  inner: T,
): z.ZodObject<{
  value: z.ZodNullable<T>;
  source: z.ZodNullable<typeof CrmDossierSourceSchema>;
  confidence: z.ZodNullable<typeof CrmDossierConfidenceSchema>;
  sourceUrl: z.ZodNullable<z.ZodString>;
}> {
  return z.object({
    value: inner.nullable(),
    source: CrmDossierSourceSchema.nullable(),
    confidence: CrmDossierConfidenceSchema.nullable(),
    /** Link para conferir o dado, quando a fonte oferece um. */
    sourceUrl: z.string().nullable(),
  });
}

export type CrmSourced<T> = {
  value: T | null;
  source: CrmDossierSource | null;
  confidence: CrmDossierConfidence | null;
  sourceUrl: string | null;
};

const sourcedString = crmSourcedSchema(z.string());
const sourcedNumber = crmSourcedSchema(z.number());
const sourcedBoolean = crmSourcedSchema(z.boolean());

// --- Bloco comum a todo coletor --------------------------------------------

const collectorBase = {
  status: CrmDossierStatusSchema,
  /** Quando ESTE bloco foi buscado — cada fonte tem seu próprio relógio. */
  fetchedAt: z.number().int().nonnegative(),
  /** Motivo legível quando `status` é `FAILED`/`SKIPPED`. */
  detail: z.string().nullable().default(null),
};

// --- Google Places ----------------------------------------------------------

/**
 * Situação do estabelecimento. É o sinal mais valioso da tela inteira: ligar
 * para uma empresa que fechou definitivamente é o desperdício mais caro do dia
 * do vendedor.
 */
export const CRM_PLACE_BUSINESS_STATUS = [
  'OPERATIONAL',
  'CLOSED_TEMPORARILY',
  'CLOSED_PERMANENTLY',
] as const;
export const CrmPlaceBusinessStatusSchema = z.enum(CRM_PLACE_BUSINESS_STATUS);
export type CrmPlaceBusinessStatus = z.infer<typeof CrmPlaceBusinessStatusSchema>;

export const CRM_PLACE_PRICE_LEVEL = [
  'FREE',
  'INEXPENSIVE',
  'MODERATE',
  'EXPENSIVE',
  'VERY_EXPENSIVE',
] as const;
export const CrmPlacePriceLevelSchema = z.enum(CRM_PLACE_PRICE_LEVEL);
export type CrmPlacePriceLevel = z.infer<typeof CrmPlacePriceLevelSchema>;

/**
 * Confiança de que o resultado é a empresa do lead, e não uma homônima.
 * `NONE` zera o bloco: avaliação confiante sobre a empresa ERRADA é pior que
 * nenhuma avaliação — a mesma regra que já governa o gate de dados completos.
 */
export const CRM_DOSSIER_MATCH = ['HIGH', 'MEDIUM', 'NONE'] as const;
export const CrmDossierMatchSchema = z.enum(CRM_DOSSIER_MATCH);
export type CrmDossierMatch = z.infer<typeof CrmDossierMatchSchema>;

/** Foto gravada no nosso Storage (aparece na aba Arquivos do lead). */
export const CrmDossierStoredPhotoSchema = z.object({
  fileId: z.string(),
  path: z.string(),
  attribution: z.string().nullable().default(null),
});
export type CrmDossierStoredPhoto = z.infer<typeof CrmDossierStoredPhotoSchema>;

export const CrmDossierPlacesSchema = z.object({
  ...collectorBase,
  placeId: z.string().nullable().default(null),
  matchConfidence: CrmDossierMatchSchema.default('NONE'),
  displayName: z.string().nullable().default(null),
  formattedAddress: z.string().nullable().default(null),
  googleMapsUri: z.string().nullable().default(null),
  websiteUri: z.string().nullable().default(null),
  phone: z.string().nullable().default(null),
  rating: z.number().nullable().default(null),
  reviewCount: z.number().int().nullable().default(null),
  businessStatus: CrmPlaceBusinessStatusSchema.nullable().default(null),
  priceLevel: CrmPlacePriceLevelSchema.nullable().default(null),
  primaryType: z.string().nullable().default(null),
  /** `weekdayDescriptions` como vêm da API — já legíveis, já no idioma pedido. */
  openingHours: z.array(z.string()).default([]),
  /** Derivado dos horários: `null` quando não há horário para decidir. */
  opensOnWeekends: z.boolean().nullable().default(null),
  photos: z.array(CrmDossierStoredPhotoSchema).default([]),
  /**
   * Quando as fotos devem ser apagadas. A política do Maps Platform permite
   * armazenar o Place ID indefinidamente, mas não o restante do conteúdo — daí
   * o TTL e o job diário que o cumpre.
   */
  photosExpireAt: z.number().int().nullable().default(null),
});
export type CrmDossierPlaces = z.infer<typeof CrmDossierPlacesSchema>;

// --- TripAdvisor (Terra API) ------------------------------------------------

/**
 * Foto do TripAdvisor exibida por **hotlink**, nunca copiada para o nosso
 * Storage automaticamente: os Termos limitam o cache a 24h, e um anexo que
 * evapora no dia seguinte é pior que nenhum. Copiar só quando o vendedor clica
 * em "Salvar nos arquivos" — aí é ato humano, não cache nosso.
 */
export const CrmDossierHotlinkPhotoSchema = z.object({
  thumbUrl: z.string(),
  largeUrl: z.string(),
  attribution: z.string().nullable().default(null),
  caption: z.string().nullable().default(null),
});
export type CrmDossierHotlinkPhoto = z.infer<typeof CrmDossierHotlinkPhotoSchema>;

/** Janela de validade do conteúdo do TripAdvisor imposta pelos Termos (24h). */
export const CRM_TRIPADVISOR_TTL_MS = 24 * 60 * 60 * 1000;

/**
 * Nota por aspecto (comida, serviço, atmosfera, custo-benefício). É o dado que
 * transforma "4,4" em conversa de venda: um restaurante com comida 4,7 e
 * serviço 3,2 tem um problema de PROCESSO — exatamente o que o EtiquetaChef
 * ajuda a organizar.
 */
export const CrmDossierSubratingSchema = z.object({
  name: z.string(),
  rating: z.number(),
  count: z.number().int().nullable().default(null),
});
export type CrmDossierSubrating = z.infer<typeof CrmDossierSubratingSchema>;

export const CrmDossierTripAdvisorSchema = z.object({
  ...collectorBase,
  locationId: z.string().nullable().default(null),
  matchConfidence: CrmDossierMatchSchema.default('NONE'),
  /** Página no TripAdvisor — a atribuição exigida pelos Termos aponta para cá. */
  url: z.string().nullable().default(null),
  rating: z.number().nullable().default(null),
  reviewCount: z.number().int().nullable().default(null),
  /** Notas por aspecto — o diferencial desta fonte sobre o Google. */
  subratings: z.array(CrmDossierSubratingSchema).default([]),
  /** Texto legível ("Fine Dining", "Cheap Eats"), não enum. */
  priceLevel: z.string().nullable().default(null),
  /** `OPEN` / `CLOSED` como a Terra devolve. */
  openStatus: z.string().nullable().default(null),
  phone: z.string().nullable().default(null),
  /** E-mail oficial do estabelecimento — a Terra entrega, o Places não. */
  email: z.string().nullable().default(null),
  website: z.string().nullable().default(null),
  address: z.string().nullable().default(null),
  /** Horários já formatados ("Mo,Tu,We 12:00-23:00"). */
  hours: z.array(z.string()).default([]),
  timezone: z.string().nullable().default(null),
  photos: z.array(CrmDossierHotlinkPhotoSchema).default([]),
});
export type CrmDossierTripAdvisor = z.infer<typeof CrmDossierTripAdvisorSchema>;

// --- Receita Federal (base CNPJ) -------------------------------------------

export const CrmDossierCnpjSchema = z.object({
  ...collectorBase,
  razaoSocial: z.string().nullable().default(null),
  nomeFantasia: z.string().nullable().default(null),
  cnaePrincipal: z.string().nullable().default(null),
  cnaePrincipalNome: z.string().nullable().default(null),
  segmento: z.string().nullable().default(null),
  grupo: z.string().nullable().default(null),
  porte: z.string().nullable().default(null),
  capitalSocial: z.number().nullable().default(null),
  dataAbertura: z.string().nullable().default(null),
  matrizFilial: z.string().nullable().default(null),
  /**
   * Estabelecimentos que compartilham o radical (8 primeiros dígitos do CNPJ) —
   * o mesmo grupo econômico. É o "número de unidades" com dado duro, em vez de
   * estimativa da IA. `null` quando a contagem não pôde ser feita.
   */
  unitsCount: z.number().int().nullable().default(null),
  socioResponsavel: z.string().nullable().default(null),
  cargo: z.string().nullable().default(null),
  socioDesde: z.string().nullable().default(null),
  qtdSocios: z.number().int().nullable().default(null),
  temSocioPj: z.boolean().nullable().default(null),
  telefoneFixo: z.string().nullable().default(null),
  celular: z.string().nullable().default(null),
  linkWhatsapp: z.string().nullable().default(null),
  email: z.string().nullable().default(null),
});
export type CrmDossierCnpj = z.infer<typeof CrmDossierCnpjSchema>;

// --- Site da empresa --------------------------------------------------------

export const CrmDossierWebsiteSchema = z.object({
  ...collectorBase,
  url: z.string().nullable().default(null),
  /** Quais páginas foram lidas — é o que permite ao vendedor conferir. */
  pagesRead: z.array(z.string()).default([]),
  phones: z.array(z.string()).default([]),
  whatsapp: z.array(z.string()).default([]),
  emails: z.array(z.string()).default([]),
  /** Redes linkadas pelo PRÓPRIO site: a evidência mais forte que existe. */
  socialLinks: z.array(z.string()).default([]),
});
export type CrmDossierWebsite = z.infer<typeof CrmDossierWebsiteSchema>;

// --- Busca na web (grounding) ----------------------------------------------

export const CrmDossierSourceLinkSchema = z.object({
  uri: z.string(),
  title: z.string(),
});
export type CrmDossierSourceLink = z.infer<typeof CrmDossierSourceLinkSchema>;

/**
 * Links achados na web. Todos passam por validação de host antes de entrar:
 * link inventado apontando para o lugar errado é pior que campo vazio.
 *
 * `instagramFollowers`, `ifoodRating` e `ifoodReviewCount` são **best-effort
 * declarado**: nem o Instagram nem o iFood expõem API pública para isso, e
 * raspar viola os Termos dos dois. Ficam `null` na maioria dos casos, e `null`
 * aqui significa "não encontrado", nunca "não tem".
 */
export const CrmDossierWebLinksSchema = z.object({
  instagram: z.string().nullable().default(null),
  instagramFollowers: z.number().int().nullable().default(null),
  facebook: z.string().nullable().default(null),
  linkedin: z.string().nullable().default(null),
  reclameAqui: z.string().nullable().default(null),
  reclameAquiNote: z.string().nullable().default(null),
  ifoodUrl: z.string().nullable().default(null),
  ifoodRating: z.number().nullable().default(null),
  ifoodReviewCount: z.number().int().nullable().default(null),
  delivery: z.array(z.object({ platform: z.string(), url: z.string() })).default([]),
  news: z.array(CrmDossierSourceLinkSchema).default([]),
});
export type CrmDossierWebLinks = z.infer<typeof CrmDossierWebLinksSchema>;

export const CrmDossierWebSearchSchema = z.object({
  ...collectorBase,
  links: CrmDossierWebLinksSchema.default(() => CrmDossierWebLinksSchema.parse({})),
  sources: z.array(CrmDossierSourceLinkSchema).default([]),
  /** Consultas que o modelo executou de fato — transparência para o vendedor. */
  queries: z.array(z.string()).default([]),
});
export type CrmDossierWebSearch = z.infer<typeof CrmDossierWebSearchSchema>;

// --- Análise (IA) -----------------------------------------------------------

export const CRM_DOSSIER_CHAIN_KIND = ['CHAIN', 'INDEPENDENT'] as const;
export const CrmDossierChainKindSchema = z.enum(CRM_DOSSIER_CHAIN_KIND);
export type CrmDossierChainKind = z.infer<typeof CrmDossierChainKindSchema>;

/**
 * Porte físico do salão, estimado pela IA a partir das FOTOS do Places. Existe
 * porque o registro (porte da Receita, capital) e o salão real divergem: uma
 * lanchonete pode ser "EPP" no papel e ter um salão grande de 200 lugares. As
 * fotos veem o que o registro não conta.
 */
export const CRM_DOSSIER_VENUE_SIZE = ['SMALL', 'MEDIUM', 'LARGE'] as const;
export const CrmDossierVenueSizeSchema = z.enum(CRM_DOSSIER_VENUE_SIZE);
export type CrmDossierVenueSize = z.infer<typeof CrmDossierVenueSizeSchema>;

/**
 * Um traço operacional inferido. `evidence` não é decoração: **campo sem
 * evidência é descartado antes de gravar**. Sem essa regra o modelo preenche o
 * silêncio com plausibilidade, que é o modo de falha caro aqui.
 */
export function crmInferredSchema<T extends z.ZodType>(
  inner: T,
): z.ZodObject<{ value: z.ZodNullable<T>; evidence: z.ZodNullable<z.ZodString> }> {
  return z.object({
    value: inner.nullable(),
    evidence: z.string().nullable(),
  });
}

export type CrmInferred<T> = { value: T | null; evidence: string | null };

export const CrmDossierOperationalSchema = z.object({
  cuisineType: crmInferredSchema(z.string()),
  hasDelivery: crmInferredSchema(z.boolean()),
  hasBuffet: crmInferredSchema(z.boolean()),
  packagedFood: crmInferredSchema(z.boolean()),
  frozenFood: crmInferredSchema(z.boolean()),
  /** O sinal que mais importa para o EtiquetaChef. */
  worksWithExpiry: crmInferredSchema(z.boolean()),
  employeesEstimate: crmInferredSchema(z.string()),
  chainOrIndependent: crmInferredSchema(CrmDossierChainKindSchema),
  /** Porte do salão visto nas fotos — o que o registro da Receita não enxerga. */
  venueSize: crmInferredSchema(CrmDossierVenueSizeSchema),
});
export type CrmDossierOperational = z.infer<typeof CrmDossierOperationalSchema>;

export const CRM_DOSSIER_CONTACT_ROLES = [
  'OWNER',
  'PARTNER',
  'MANAGER',
  'NUTRITIONIST',
  'QUALITY',
  'DECISION_MAKER',
  'OTHER',
] as const;
export const CrmDossierContactRoleSchema = z.enum(CRM_DOSSIER_CONTACT_ROLES);
export type CrmDossierContactRole = z.infer<typeof CrmDossierContactRoleSchema>;
/**
 * Papel de um contato no dossiê como constantes (`CrmDossierContactRole.PARTNER`).
 *
 * Origem: #761 — 2026-09-27 17:09 +0200 (a lista é do dossiê, #202,
 * `151f2adec`, 2026-07-22).
 *
 * Por quê: aqui `PARTNER` é **sócio** da empresa do lead — não o parceiro de
 * revenda (`CrmRole.PARTNER`), nem o modo do tenant. É o caso em que o mesmo
 * texto mais engana; a constante diz o conceito.
 *
 * Usado por: CRM Functions — `lib/companyDossier/score.ts` (quem decide),
 * `extract.ts` (o enum mandado ao modelo) e `consolidate.ts`.
 *
 * Issues: #761, #202.
 */
export const CrmDossierContactRole = CrmDossierContactRoleSchema.enum;

export const CrmDossierAiContactSchema = z.object({
  role: CrmDossierContactRoleSchema,
  name: z.string().nullable().default(null),
  phone: z.string().nullable().default(null),
  email: z.string().nullable().default(null),
  linkedin: z.string().nullable().default(null),
  evidence: z.string().nullable().default(null),
});
export type CrmDossierAiContact = z.infer<typeof CrmDossierAiContactSchema>;

export const CrmDossierAiSchema = z.object({
  ...collectorBase,
  model: z.string().nullable().default(null),
  operational: CrmDossierOperationalSchema.nullable().default(null),
  contacts: z.array(CrmDossierAiContactSchema).default([]),
  /** Briefing de venda em prosa — o que sobrou (e ainda vale) da v1. */
  briefing: z.string().nullable().default(null),
});
export type CrmDossierAi = z.infer<typeof CrmDossierAiSchema>;

// --- Consolidado ------------------------------------------------------------

export const CrmDossierContactSchema = z.object({
  role: CrmDossierContactRoleSchema,
  name: sourcedString,
  phone: sourcedString,
  email: sourcedString,
  linkedin: sourcedString,
});
export type CrmDossierContact = z.infer<typeof CrmDossierContactSchema>;

export const CRM_DOSSIER_BANDS = ['HIGH', 'MEDIUM', 'LOW'] as const;
export const CrmDossierBandSchema = z.enum(CRM_DOSSIER_BANDS);
export type CrmDossierBand = z.infer<typeof CrmDossierBandSchema>;

/**
 * Uma métrica da pontuação comercial. `factors` existe para a UI **explicar** o
 * número: um score sem justificativa é um oráculo, e ninguém confia a agenda do
 * dia a um oráculo.
 */
export const CrmDossierMetricSchema = z.object({
  score: z.number().int().min(0).max(100),
  band: CrmDossierBandSchema,
  factors: z.array(z.string()).default([]),
});
export type CrmDossierMetric = z.infer<typeof CrmDossierMetricSchema>;

export const CrmDossierScoreSchema = z.object({
  etiquetachefPotential: CrmDossierMetricSchema,
  urgency: CrmDossierMetricSchema,
  sizeEstimate: CrmDossierMetricSchema,
  digitalMaturity: CrmDossierMetricSchema,
  contactQuality: CrmDossierMetricSchema,
});
export type CrmDossierScore = z.infer<typeof CrmDossierScoreSchema>;

/**
 * Divergência entre fontes (ex.: telefone do Places ≠ telefone da Receita).
 * Exibida, não escondida: saber que duas fontes discordam é informação — e
 * escolher em silêncio uma delas seria decidir no lugar do vendedor.
 */
export const CrmDossierConflictSchema = z.object({
  field: z.string(),
  values: z.array(z.object({ value: z.string(), source: CrmDossierSourceSchema })),
});
export type CrmDossierConflict = z.infer<typeof CrmDossierConflictSchema>;

export const CrmDossierConsolidatedSchema = z.object({
  website: sourcedString,
  phone: sourcedString,
  whatsapp: sourcedString,
  email: sourcedString,
  establishmentType: sourcedString,
  cuisineType: sourcedString,
  unitsCount: sourcedNumber,
  opensOnWeekends: sourcedBoolean,
  contacts: z.array(CrmDossierContactSchema).default([]),
  score: CrmDossierScoreSchema.nullable().default(null),
  conflicts: z.array(CrmDossierConflictSchema).default([]),
});
export type CrmDossierConsolidated = z.infer<typeof CrmDossierConsolidatedSchema>;

// --- Documento --------------------------------------------------------------

export const CrmCompanyDossierSchema = z.object({
  sources: z.object({
    places: CrmDossierPlacesSchema,
    tripadvisor: CrmDossierTripAdvisorSchema,
    cnpj: CrmDossierCnpjSchema,
    website: CrmDossierWebsiteSchema,
    websearch: CrmDossierWebSearchSchema,
    ai: CrmDossierAiSchema,
  }),
  consolidated: CrmDossierConsolidatedSchema,
  researchedBy: z.string(),
  researchedAt: z.number().int().nonnegative(),
});
export type CrmCompanyDossier = z.infer<typeof CrmCompanyDossierSchema>;

/** Id fixo do doc em `crm_leads/{leadId}/crm_research` — um dossiê por lead. */
export const CRM_DOSSIER_DOC_ID = 'latest';

/**
 * Cooldown curto contra clique duplo/acidental: dentro dele, repesquisar sem
 * `force` devolve o dossiê gravado em vez de pagar tudo de novo.
 */
export const CRM_DOSSIER_COOLDOWN_MS = 10 * 60 * 1000;

/**
 * Janela de FRESCOR da pesquisa completa: 24h. O botão "Atualizar pesquisa" só
 * reabilita depois disso, e o servidor recusa uma pesquisa completa nova antes
 * do prazo mesmo com `force`.
 *
 * Duas razões que se somam: custo (uma pesquisa completa é ~US$0,10, e não faz
 * sentido refazer de hora em hora) e frescor real dos dados — 24h é o TTL do
 * conteúdo mais volátil do dossiê (o TripAdvisor, cujos Termos limitam o cache
 * a exatamente isso). Reconsultar UMA fonte específica não passa por aqui: é
 * correção pontual e continua livre.
 */
export const CRM_DOSSIER_FULL_REFRESH_MS = 24 * 60 * 60 * 1000;

// --- Contratos das callables ------------------------------------------------

/**
 * `collectors` vazio = pesquisa completa. Preenchido, recoleta só as fontes
 * pedidas — é o botão "reconsultar" de cada card, que não deve pagar as outras.
 */
export const CRM_DOSSIER_COLLECTORS = [
  'places',
  'tripadvisor',
  'cnpj',
  'website',
  'websearch',
  'ai',
] as const;
export const CrmDossierCollectorSchema = z.enum(CRM_DOSSIER_COLLECTORS);
export type CrmDossierCollector = z.infer<typeof CrmDossierCollectorSchema>;

export const CrmResearchCompanyInput2Schema = z.object({
  boardId: z.string().min(1),
  leadId: z.string().min(1),
  collectors: z.array(CrmDossierCollectorSchema).default([]),
  /** Ignora o cooldown — o botão "Atualizar pesquisa" explícito do vendedor. */
  force: z.boolean().default(false),
});
export type CrmResearchCompanyInput2 = z.infer<typeof CrmResearchCompanyInput2Schema>;

export type CrmResearchCompanyResult2 = {
  dossier: CrmCompanyDossier;
  /** `true` quando o cooldown devolveu o dossiê gravado sem chamar ninguém. */
  cached: boolean;
};

/** Anexa ao lead uma foto que hoje só existe por hotlink (TripAdvisor). */
export const CrmSaveDossierPhotoInputSchema = z.object({
  boardId: z.string().min(1),
  leadId: z.string().min(1),
  url: z.url(),
  attribution: z.string().max(200).nullable().default(null),
});
export type CrmSaveDossierPhotoInput = z.infer<typeof CrmSaveDossierPhotoInputSchema>;

/**
 * De onde vem a foto que o vendedor escolheu para virar a foto de perfil do
 * lead. As duas galerias do dossiê guardam a foto de jeitos diferentes, e o
 * servidor precisa saber qual é qual: uma se copia dentro do nosso Storage, a
 * outra se baixa de fora.
 */
export const CrmDossierLogoSourceSchema = z.discriminatedUnion('kind', [
  /** Google Places: a foto já é nossa, em `crm_boards/{boardId}/attachments/`. */
  z.object({ kind: z.literal('STORAGE_PATH'), path: z.string().min(1).max(500) }),
  /** TripAdvisor: a foto só existe por hotlink e é baixada na hora. */
  z.object({ kind: z.literal('EXTERNAL_URL'), url: z.url() }),
]);
export type CrmDossierLogoSource = z.infer<typeof CrmDossierLogoSourceSchema>;

/**
 * Promove uma foto do dossiê a foto de perfil do lead (`lead.logoPath`).
 *
 * A foto vira uma CÓPIA nova em `lead_logos/`, e não uma referência à foto da
 * galeria: as fotos do Places têm prazo de 30 dias (`crmScanDossierPhotos`
 * apaga), e um logo que some sozinho depois de um mês seria pior que não ter a
 * funcionalidade. O prazo da galeria continua valendo para a galeria.
 */
export const CrmSetLeadLogoFromDossierInputSchema = z.object({
  boardId: z.string().min(1),
  leadId: z.string().min(1),
  photo: CrmDossierLogoSourceSchema,
});
export type CrmSetLeadLogoFromDossierInput = z.infer<typeof CrmSetLeadLogoFromDossierInputSchema>;
